'use client'

import { ReactNode, useContext } from 'react'
import { useStore } from 'zustand'
import { useQueryClient } from '@tanstack/react-query'
import { v4 } from 'uuid'
import moment from 'moment-timezone'
import { StoreContext } from '@/app/_providers/StoreProvider'
import StepContext from '@/app/_providers/StepProvider/StepContext'
import { useEventIds, useSelectedEventId } from '@/app/_providers/EventProvider/EventProvider'
import { useSeller } from '@/app/_providers/SellerProvider/SellerProvider'
import { CartResetType, useReset } from '@/hooks/useReset'
import { AdmissionType } from '@/types/general.types'
import { QUERY_KEYS } from '@/config/tanstack-querykeys.config'
import { DAYPASS_URL_PARAM, TIMED_TICKET_URL_PARAM } from '@/config/navigation.config'
import s from './DebugPanel.module.css'

const CURRENT_DATE = moment().format('YYYY-MM-DDTHH:mm:ssZ')
const ELAPSED_DATE = '2026-05-18T16:00:00Z'
const ELAPSED_START_TIME = '2026-05-18T16:00:00Z'

const StoreDebug = (): ReactNode => {
  const store = useContext(StoreContext)
  const state = useStore(store!, (st) => st)
  const eventIds = useEventIds()
  const selectedEventId = useSelectedEventId()
  const queryClient = useQueryClient()
  const { timezone } = useSeller()
  const { doReset } = useReset()
  const { getStepsMetaForId } = useContext(StepContext)

  // Timeslot and calendar pricing are computed server side, so the cached
  // responses have to go whenever the selected event changes.
  const refetchPricing = (): void => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TIMESLOTS] })
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CALENDAR_META] })
  }

  const admissionTypeLabel = (): string => (
    state.admissionType === AdmissionType.DayPass ? 'day pass' : 'timed ticket'
  )

  const selectedEventLabel = (): string => {
    if (selectedEventId === eventIds.dayPass) return 'day pass'
    if (selectedEventId === eventIds.timedTicket) return 'timed ticket'
    return selectedEventId || 'none'
  }

  return (
    <details className={s.panel} aria-label="Actions panel">
      <summary className={s.summary}>
        <span className={s.summaryIcon} aria-hidden="true">&#9654;</span>
        <span className={s.summaryTitle}>Actions</span>
      </summary>
      <div className={s.body}>
        <div className={s.row}>
          <button
            className={s.button}
            type="button"
            onClick={() => state.resetAll()}
          >
            Reset Store
          </button>
        </div>
        <div className={s.row}>
          <button
            className={s.button}
            type="button"
            onClick={() => state.updatePartial({ activeDate: ELAPSED_DATE })}
          >
            Set Elapsed Date
          </button>
        </div>
        <div className={s.row}>
          <button
            className={s.button}
            type="button"
            onClick={() => state.updatePartial({
              activeDate: CURRENT_DATE,
              activeStartTime: {
                id: v4(),
                startTime: ELAPSED_START_TIME,
                remaining: 100,
                endTime: CURRENT_DATE,
                endTimeWithOffset: CURRENT_DATE,
                eventId: v4(),
              },
            })}
          >
            Set Elapsed Start time
          </button>
        </div>
        <div className={s.row}>
          <button
            className={s.button}
            type="button"
            onClick={() => state.setCartId(v4(), false)}
          >
            Set Guest Cart ID
          </button>
        </div>
        <div className={s.row}>
          <button
            className={s.button}
            type="button"
            onClick={() => state.updatePartial({ lastStepCompleted: 3 })}
          >
            Set Last Step Completed
          </button>
        </div>

        {/*
          Sold out day pass repro. The bug was that the timed ticket grid rendered off
          `admissionType` while timeslot pricing was fetched off a separate `selectedEventId`,
          so a day pass left selected made every timeslot read "Pricing unavailable".
          The event is now derived from `admissionType`, so the readout below follows the flow
          by construction and the broken pair can no longer be staged.
          See docs/day-pass-pricing-bug.md.
        */}
        <div className={s.row}>
          <span className={s.key}>pricing event</span>
          <span className={s.val}>{selectedEventLabel()}</span>
        </div>
        <div className={s.row}>
          <button
            className={s.button}
            type="button"
            disabled={!eventIds.dayPass}
            onClick={() => {
              // The real path: pick a day pass, then let the elapsed/sold out reset fire.
              state.resetAdmissionType({ admissionType: AdmissionType.DayPass })
              doReset({ type: CartResetType.StartTimeAndLocalItems }).then(refetchPricing)
            }}
          >
            Day pass, then elapsed reset
          </button>
        </div>

        {/*
          Walks the admission_type deep link pair a user hits — click once for day pass, again for
          timed ticket — as full page loads rather than an in-place toggle.

          Kept as standing cover for the flow switch. `timeslotsMap` is now derived with useMemo in
          Timeslots, so it cannot go stale across a switch; it used to be cached in a reducer and
          rebuilt only by the fetch completion callback, and because the timeslots query key has no
          admission type in it (useEventIds returns both ids regardless of flow) a switch reused the
          cached response and the callback never re-ran. Coming from day pass that left the time
          step with a day pass button, an "or", and nothing else.

          Full loads matter: reproducing that original bug needed Timeslots to MOUNT in the day pass
          flow. Toggling in place left an already-built map behind and polling eventually rebuilt it
          — both hid it. See docs/admission-type-switching-bug.md.
        */}
        <div className={s.row}>
          <span className={s.key}>admission type</span>
          <span className={s.val}>{admissionTypeLabel()}</span>
        </div>
        <div className={s.row}>
          <button
            className={s.button}
            type="button"
            disabled={!eventIds.dayPass}
            onClick={() => {
              const admissionType = state.admissionType === AdmissionType.DayPass
                ? TIMED_TICKET_URL_PARAM
                : DAYPASS_URL_PARAM
              // activeDate is stored as venue-local 10:00 normalised to UTC, so it has to be read
              // back in the venue's timezone — plain moment() formats in the browser's and a
              // viewer east of the venue would deep-link the following day.
              const date = state.activeDate
                ? moment.tz(state.activeDate, timezone).format('YYYY-MM-DD')
                : ''
              const params = new URLSearchParams()
              params.set('admission_type', admissionType)
              if (date) params.set('date', date)
              window.location.assign(`${window.location.pathname}?${params.toString()}`)
            }}
          >
            {state.admissionType === AdmissionType.DayPass
              ? 'Deep link: timed_ticket'
              : 'Deep link: daypass'}
          </button>
        </div>

        {/*
          Day pass flow, then an event's link back to the exhibition. `EventTickets` builds that
          link as `?date=&time=` with no admission_type (see `createDateString`), so nothing in the
          URL says which flow to use and the store's persisted day pass used to win: the day pass
          auto-select overwrote the deep-linked start time and pushed the user to the day pass
          ticket step, which then read "Sorry, we couldn't find that session" — the URL-seeded
          activeStartTime carries no session id and the day pass event has no session at that time.
          An incoming time only means something in timed ticketing, so it has to win.

          Pick a timed ticket slot first: this reuses that start time, which guarantees the deep
          link names a real bookable slot, exactly as the event link does. The switch to day pass
          happens before the navigation so the reload starts from a persisted day pass, which is
          the state the bug needs — an in-place toggle cannot stage it.
        */}
        <div className={s.row}>
          <button
            className={s.button}
            type="button"
            disabled={!state.activeStartTime.startTime}
            onClick={() => {
              if (!state.activeStartTime.startTime) return
              // activeStartTime is UTC; both halves of the link have to be read back in the venue's
              // timezone or a viewer in another zone deep-links the wrong day or hour.
              const startTime = moment.tz(state.activeStartTime.startTime, timezone)
              const params = new URLSearchParams({
                date: startTime.format('YYYY-MM-DD'),
                time: startTime.format('HH:mm'),
              })
              state.resetAdmissionType({ admissionType: AdmissionType.DayPass })
              const route = getStepsMetaForId('/')?.route || window.location.pathname
              window.location.assign(`${route}?${params.toString()}`)
            }}
          >
            Day pass, then event link with date &amp; time
          </button>
        </div>
      </div>
    </details>
  )
}

export default StoreDebug
